import { useLayoutEffect, useRef, useState } from 'preact/hooks'
import { text } from '#contract'
import { useView } from '../app/use-view.ts'
import { useT } from '../i18n/index.ts'
import { sideView } from './inventory-grid.tsx'
import styles from './item-tooltip.module.css'
import { useScreenUi } from './screen-context.ts'

/** Hovering or focusing a slot this long shows its tooltip. */
const DELAY_MS = 250
/** Between the slot and the tooltip, and the tooltip and the screen's edge. */
const GAP_PX = 8

/**
 * What's in the hovered (or focused) slot: the name, `count / stackSize`, its tags and
 * whether it's consumable. Beside the slot, flipped to stay on screen. Hidden while the
 * cursor holds a stack.
 */
export function ItemTooltip() {
  const t = useT()
  const ui = useScreenUi()
  const hover = ui.hover.value
  const slot = useView((view) => {
    const target = ui.hover.value?.target
    if (!target || target === 'bin' || view.inventory?.screen?.cursor) return null
    return sideView(view, target.side)?.slots[target.slot] ?? null
  })
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(false)

  useLayoutEffect(() => {
    setShown(false)
    if (!slot || !hover) return
    const timer = setTimeout(() => {
      setShown(true)
    }, DELAY_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [slot, hover])

  useLayoutEffect(() => {
    const element = ref.current
    if (!element || !hover || !shown) return
    const at = place(hover.element.getBoundingClientRect(), element.getBoundingClientRect())
    element.style.left = `${String(at.x)}px`
    element.style.top = `${String(at.y)}px`
  }, [hover, shown])

  if (!slot || !shown) return null
  const { item, count } = slot
  return (
    <div ref={ref} class={styles.tooltip} role="tooltip">
      <p class={styles.name}>{t(item.name)}</p>
      <p class={styles.count}>{t(text('inventory.count', { count, max: item.stackSize }))}</p>
      {(item.tags.length > 0 || item.consumable) && (
        <ul class={styles.tags}>
          {item.consumable && <li class={styles.consumable}>{t(text('inventory.consumable'))}</li>}
          {item.tags.map((tag) => (
            <li key={tag}>{t(text(`inventory.tag.${tag}`))}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Right of the slot, else left of it; top-aligned with it, kept inside the viewport. */
function place(slot: DOMRect, tip: DOMRect): { x: number; y: number } {
  const right = slot.right + GAP_PX
  const x =
    right + tip.width + GAP_PX <= window.innerWidth
      ? right
      : Math.max(GAP_PX, slot.left - GAP_PX - tip.width)
  const y = Math.min(Math.max(GAP_PX, slot.top), window.innerHeight - tip.height - GAP_PX)
  return { x, y }
}
